import React from 'react';
import { CalendarX, Plus, Trash2 } from 'lucide-react';
import { WEEKDAYS_SHORT, fmtDate } from './constants';

const BlockedDatesTab = ({
  recurring, blocked, newBlock, busy,
  onToggleRecurring, onNewBlockChange, onAddBlocked, onRemoveBlocked,
}) => (
  <div className="space-y-6" data-testid="blocked-dates-tab">
    <div className="bg-white border border-black/5 p-6">
      <div className="flex items-center gap-2 mb-3">
        <CalendarX size={18} className="text-[#c9a961]" strokeWidth={1.5} />
        <h3 className="font-serif text-xl text-[#1c1e20]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Recurring days off
        </h3>
      </div>
      <p className="text-[13px] text-[#6b6b6b] mb-5">
        Tap a weekday to block every occurrence of it. Great for standing days off like every Sunday.
      </p>
      <div className="flex flex-wrap gap-2">
        {WEEKDAYS_SHORT.map((label, wd) => {
          const on = recurring.some((r) => r.weekday === wd);
          return (
            <button
              key={wd}
              onClick={() => onToggleRecurring(wd)}
              data-testid={`recurring-toggle-${wd}`}
              className={`px-5 py-2.5 text-[11px] tracking-[0.24em] font-semibold border transition-colors ${
                on
                  ? 'bg-[#c9a961] border-[#c9a961] text-[#141618]'
                  : 'bg-white border-black/15 text-[#3c3c3c] hover:border-[#c9a961]'
              }`}
            >
              {label.toUpperCase()}
            </button>
          );
        })}
      </div>
      {recurring.length > 0 && (
        <p className="text-[12px] text-[#8a7132] mt-4">
          Currently blocking: {recurring.map((r) => WEEKDAYS_SHORT[r.weekday] + 's').join(', ')}
        </p>
      )}
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <form onSubmit={onAddBlocked} className="bg-white border border-black/5 p-6" data-testid="block-date-form">
        <div className="flex items-center gap-2 mb-4">
          <CalendarX size={18} className="text-[#c9a961]" strokeWidth={1.5} />
          <h3 className="font-serif text-xl text-[#1c1e20]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Block a specific date
          </h3>
        </div>
        <label className="block text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-2">DATE</label>
        <input
          type="date"
          required
          value={newBlock.date}
          onChange={(e) => onNewBlockChange({ ...newBlock, date: e.target.value })}
          data-testid="block-date-input"
          className="w-full border border-black/15 px-4 py-2.5 mb-4 focus:outline-none focus:border-[#c9a961]"
        />
        <label className="block text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-2">REASON (OPTIONAL)</label>
        <input
          type="text"
          value={newBlock.reason}
          onChange={(e) => onNewBlockChange({ ...newBlock, reason: e.target.value })}
          placeholder="Holiday, team training, etc."
          data-testid="block-reason-input"
          className="w-full border border-black/15 px-4 py-2.5 mb-5 focus:outline-none focus:border-[#c9a961]"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="block-date-submit"
          className="w-full flex items-center justify-center gap-2 bg-[#c9a961] hover:bg-[#b89751] transition-colors text-[#141618] tracking-[0.22em] text-[11px] font-semibold py-3 disabled:opacity-60"
        >
          <Plus size={14} /> BLOCK THIS DATE
        </button>
        <p className="text-[11px] text-[#6b6b6b] mt-3">Clients cannot book this date on the contact form.</p>
      </form>

      <div className="md:col-span-2 bg-white border border-black/5">
        <div className="px-5 py-4 border-b border-black/5">
          <h3 className="font-serif text-xl text-[#1c1e20]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Currently blocked ({blocked.length})
          </h3>
        </div>
        {blocked.length === 0 ? (
          <p className="text-center text-[#6b6b6b] py-10 text-sm">No specific dates are currently blocked.</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.22em] text-[#6b6b6b] border-b border-black/10">
                <th className="px-5 py-3 font-semibold">DATE</th>
                <th className="px-5 py-3 font-semibold">REASON</th>
                <th className="px-5 py-3 font-semibold text-right">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {blocked.map((b) => (
                <tr key={b.id} className="border-b border-black/5">
                  <td className="px-5 py-3 font-semibold text-[#1c1e20]">{fmtDate(b.date)}</td>
                  <td className="px-5 py-3 text-[#3c3c3c]">{b.reason || '—'}</td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => onRemoveBlocked(b.id)}
                      data-testid={`unblock-date-${b.id}`}
                      className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 text-[12px] font-semibold"
                    >
                      <Trash2 size={13} /> UNBLOCK
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </div>
);

export default BlockedDatesTab;
